import { useCallback, useEffect, useRef, useState } from "react";
import type { ServerMessage, TransferFile } from "@airspace/shared";
import { ApiRequestError } from "./client.js";
import {
  BULK_DOWNLOAD_URL,
  ChunkUploader,
  downloadToDisk,
  downloadUrl,
  throwUploadBackend,
  transferApi,
  type UploadProgress,
} from "./transfer.js";

/** Zustand des Sammeldownloads aller empfangenen Dateien. */
export interface BulkDownloadState {
  running: boolean;
  receivedBytes: number;
  /** Summe der Dateigroessen -- das Archiv wird geringfuegig groesser. */
  totalBytes: number;
  error?: string | undefined;
}

export interface TransferEntry extends TransferFile {
  // `| undefined` ist unter exactOptionalPropertyTypes noetig, damit ein Feld
  // ausdruecklich auf undefined zurueckgesetzt werden kann -- etwa um einen
  // alten Fehler zu loeschen.
  /** Fortschritt des laufenden Uploads, nur fuer eigene Dateien. */
  upload?: UploadProgress | undefined;
  /** Fortschritt eines laufenden Downloads. */
  downloadedBytes?: number | undefined;
  /** Zuletzt aufgetretener Fehler zu dieser Datei. */
  error?: string | undefined;
}

/**
 * Verwaltet die Dateien einer Kopplung.
 *
 * Die Wahrheit ueber den Zustand kommt vom Server -- ueber den WebSocket, und
 * nach jedem Neuladen ueber die Liste. Lokal kommen nur die Dinge dazu, die der
 * Server nicht kennen kann: der Fortschritt des eigenen Uploads und der eines
 * laufenden Downloads.
 */
export function useTransfers(
  subscribe: (handler: (message: ServerMessage) => void) => () => void,
  pairingId: string | null,
) {
  const [files, setFiles] = useState<TransferEntry[]>([]);

  /** Fortschritt des Sammeldownloads. Betrifft alle Dateien zugleich. */
  const [bulk, setBulk] = useState<BulkDownloadState>({
    running: false,
    receivedBytes: 0,
    totalBytes: 0,
  });

  /**
   * Laufende Uploads.
   *
   * Bewusst in einer Ref und nicht im Zustand: ein Uploader ist ein
   * veraenderliches Objekt mit einer laufenden Schleife, kein Renderwert.
   *
   * Das bedeutet zugleich: die `File`-Objekte leben nur in dieser Sitzung. Nach
   * einem Neuladen kann ein angefangener eigener Upload nicht fortgesetzt
   * werden -- der Browser gibt die Datei ohne erneute Auswahl nicht wieder her.
   * Die Oberflaeche sagt das offen, statt eine Wiederaufnahme vorzutaeuschen.
   */
  const uploaders = useRef(new Map<string, ChunkUploader>());

  const patch = useCallback((fileId: string, changes: Partial<TransferEntry>) => {
    setFiles((current) =>
      current.map((entry) => (entry.fileId === fileId ? { ...entry, ...changes } : entry)),
    );
  }, []);

  const reload = useCallback(async () => {
    if (!pairingId) {
      setFiles([]);
      return;
    }
    try {
      const data = await transferApi.list();
      setFiles((current) =>
        data.files.map((file) => {
          // Lokale Zusatzinfos erhalten, damit ein Neuladen den angezeigten
          // Fortschritt nicht zurueckwirft.
          const existing = current.find((entry) => entry.fileId === file.fileId);
          return existing ? { ...existing, ...file } : file;
        }),
      );
    } catch {
      // Ohne Kopplung liefert der Endpunkt eine leere Liste; alles andere
      // faellt beim naechsten Durchlauf auf.
    }
  }, [pairingId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // --- Servernachrichten -----------------------------------------------------

  useEffect(() => {
    return subscribe((message) => {
      if (message.type === "file.offered") {
        setFiles((current) =>
          current.some((entry) => entry.fileId === message.file.fileId)
            ? current
            : [...current, message.file],
        );
        return;
      }

      if (message.type === "file.status") {
        setFiles((current) =>
          current.map((entry) =>
            entry.fileId === message.fileId
              ? {
                  ...entry,
                  status: message.status,
                  receivedBytes: message.receivedBytes,
                  sha256: message.sha256,
                }
              : entry,
          ),
        );

        // Sobald der Empfaenger zugestimmt hat, laeuft der Upload los. Vorher
        // ist bewusst kein Byte geflossen.
        if (message.status === "accepted") {
          const uploader = uploaders.current.get(message.fileId);
          if (uploader && uploader.state === "idle") void uploader.run();
        }

        if (message.status === "rejected" || message.status === "cancelled") {
          uploaders.current.get(message.fileId)?.abort();
          uploaders.current.delete(message.fileId);
        }
      }
    });
  }, [subscribe]);

  // --- Aktionen --------------------------------------------------------------

  /** Kuendigt Dateien an. Der Upload beginnt erst nach der Zustimmung. */
  const offer = useCallback(
    async (selection: File[]) => {
      for (const file of selection) {
        try {
          const { file: offered } = await transferApi.offer(file);
          setFiles((current) => [...current, offered]);

          const uploader = new ChunkUploader(
            offered.fileId,
            file,
            throwUploadBackend(offered.fileId),
            (progress) => {
              patch(offered.fileId, {
                upload: progress,
                ...(progress.error ? { error: progress.error } : {}),
              });
            },
          );
          uploaders.current.set(offered.fileId, uploader);
        } catch (error) {
          const message =
            error instanceof ApiRequestError ? error.message : "Die Datei wurde abgelehnt.";
          // Ohne Server-Eintrag gibt es keine fileId -- deshalb ein rein
          // lokaler Platzhalter, damit der Fehlschlag sichtbar bleibt.
          setFiles((current) => [
            ...current,
            {
              fileId: `lokal-${crypto.randomUUID()}`,
              direction: "outgoing",
              name: file.name,
              size: file.size,
              mimeType: file.type || null,
              status: "failed",
              receivedBytes: 0,
              sha256: null,
              createdAt: new Date().toISOString(),
              expiresAt: new Date().toISOString(),
              error: message,
            },
          ]);
        }
      }
    },
    [patch],
  );

  const accept = useCallback(
    async (fileId: string) => {
      try {
        await transferApi.accept(fileId);
      } catch (error) {
        patch(fileId, {
          error: error instanceof ApiRequestError ? error.message : "Annehmen fehlgeschlagen.",
        });
      }
    },
    [patch],
  );

  const reject = useCallback(
    async (fileId: string) => {
      try {
        await transferApi.reject(fileId);
      } catch (error) {
        patch(fileId, {
          error: error instanceof ApiRequestError ? error.message : "Ablehnen fehlgeschlagen.",
        });
      }
    },
    [patch],
  );

  const cancel = useCallback(async (fileId: string) => {
    uploaders.current.get(fileId)?.abort();
    uploaders.current.delete(fileId);
    try {
      await transferApi.cancel(fileId);
    } catch {
      // Ist die Datei bereits beendet, ist das Ziel ohnehin erreicht.
    }
  }, []);

  const pause = useCallback((fileId: string) => {
    uploaders.current.get(fileId)?.pause();
  }, []);

  const resume = useCallback((fileId: string) => {
    const uploader = uploaders.current.get(fileId);
    if (!uploader) return;
    if (uploader.state === "paused") uploader.resume();
    // Nach einem Fehlschlag von vorn anfangen -- der Uploader holt sich den
    // tatsaechlichen Offset ohnehin beim Server ab.
    else if (uploader.state === "error") void uploader.run();
  }, []);

  const download = useCallback(
    async (file: TransferEntry) => {
      patch(file.fileId, { downloadedBytes: 0, error: undefined });
      try {
        const result = await downloadToDisk(
          { url: downloadUrl(file.fileId), name: file.name, totalBytes: file.size },
          (progress) => patch(file.fileId, { downloadedBytes: progress.receivedBytes }),
        );
        if (result === "delegated") patch(file.fileId, { downloadedBytes: undefined });
      } catch (error) {
        // Ein abgebrochener Speichern-Dialog ist kein Fehler, sondern eine
        // Entscheidung des Nutzers.
        const aborted = error instanceof DOMException && error.name === "AbortError";
        patch(file.fileId, {
          downloadedBytes: undefined,
          ...(aborted
            ? {}
            : {
                error:
                  error instanceof Error ? error.message : "Der Download ist fehlgeschlagen.",
              }),
        });
      }
    },
    [patch],
  );

  /**
   * Laedt alle empfangenen Dateien als ein Archiv.
   *
   * Der Fortschritt bezieht sich auf die Summe der Dateigroessen. Das Archiv
   * wird geringfuegig groesser -- die Anzeige ist eine Schaetzung, keine
   * Buchhaltung.
   */
  const downloadAll = useCallback(async () => {
    const ready = files.filter((f) => f.direction === "incoming" && f.status === "complete");
    if (ready.length === 0) return;

    const totalBytes = ready.reduce((sum, f) => sum + f.size, 0);
    const stamp = new Date().toISOString().slice(0, 10);

    setBulk({ running: true, receivedBytes: 0, totalBytes, error: undefined });
    try {
      const result = await downloadToDisk(
        { url: BULK_DOWNLOAD_URL, name: `airspace-${stamp}.zip`, totalBytes },
        (progress) =>
          setBulk((current) => ({ ...current, receivedBytes: progress.receivedBytes })),
      );
      setBulk({ running: false, receivedBytes: 0, totalBytes: 0, error: undefined });
      return result;
    } catch (error) {
      const aborted = error instanceof DOMException && error.name === "AbortError";
      setBulk({
        running: false,
        receivedBytes: 0,
        totalBytes: 0,
        error: aborted
          ? undefined
          : error instanceof Error
            ? error.message
            : "Der Download ist fehlgeschlagen.",
      });
    }
    return undefined;
  }, [files]);

  /** Ob zu dieser Datei ein Uploader in dieser Sitzung existiert. */
  const canResumeUpload = useCallback(
    (fileId: string) => uploaders.current.has(fileId),
    [],
  );

  return {
    files,
    bulk,
    actions: {
      offer,
      accept,
      reject,
      cancel,
      pause,
      resume,
      download,
      downloadAll,
      reload,
      canResumeUpload,
    },
  };
}
